import type { TranslationKey } from '@/i18n-key';

import {
    EvaluationType,
    type EvaluationTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import {
    CycleMilestoneType,
    type CycleMilestoneTypeValue,
} from '@/constants/cycle-evaluation/CycleMilestoneType';
export { getCheckedEvaluationTypes } from '@/pages/CycleEvaluation/Deadlines/milestones';

export const CommunicationBlockId = {
    PROJECT_KICK_OFF: 'projectKickOff',
    SELF_ASSESSMENT_LAUNCH: 'selfAssessmentLaunch',
    FEEDBACK_360_LAUNCH: 'feedback360Launch',
    MANAGER_ASSESSMENT_LAUNCH: 'managerAssessmentLaunch',
    MANAGER_DEADLINE: 'managerDeadline',
    DISCUSSION_EMAIL: 'discussionEmail',
    EVALUATION_CYCLE_CLOSURE: 'evaluationCycleClosure',
} as const;

export type CommunicationBlockIdValue =
    (typeof CommunicationBlockId)[keyof typeof CommunicationBlockId];

export const CommunicationBlockKind = {
    ORPHAN: 'orphan',
    LAUNCH: 'launch',
} as const;

export const CommunicationTemplateSource = {
    /** Reuses an existing, already-approved template (manager-notification / self / 360 defaults). */
    REUSED: 'reused',
    /** Provisional minimal fallback (logo + text), until an approved copy exists for these blocks. */
    MINIMAL: 'minimal',
} as const;

export type CommunicationTemplateSourceValue =
    (typeof CommunicationTemplateSource)[keyof typeof CommunicationTemplateSource];

export const CommunicationBlockPersistenceRoute = {
    /** The new `CycleCommunicationBlock` contract: five of the seven slots. */
    COMMUNICATIONS_LAYER: 'communicationsLayer',
    /** Both cards, through the existing CycleSchedule fields (manager and employee pairs alike). */
    LEGACY_SCHEDULE: 'legacySchedule',
} as const;

export type CommunicationBlockPersistenceRouteValue =
    (typeof CommunicationBlockPersistenceRoute)[keyof typeof CommunicationBlockPersistenceRoute];

interface BaseCommunicationBlockDefinition {
    id: CommunicationBlockIdValue;
    labelKey: TranslationKey;
    /**
     * Whether this block has an employee card (per the corrected matrix).
     */
    hasEmployeeCard: boolean;
    templateSource: CommunicationTemplateSourceValue;
    persistenceRoute: CommunicationBlockPersistenceRouteValue;
}

export interface OrphanCommunicationBlockDefinition extends BaseCommunicationBlockDefinition {
    kind: typeof CommunicationBlockKind.ORPHAN;
    milestoneType: CycleMilestoneTypeValue;
}

export interface LaunchCommunicationBlockDefinition extends BaseCommunicationBlockDefinition {
    kind: typeof CommunicationBlockKind.LAUNCH;
    evaluationType: EvaluationTypeValue;
}

export type CommunicationBlockDefinition =
    | OrphanCommunicationBlockDefinition
    | LaunchCommunicationBlockDefinition;

export const isLaunchCommunicationBlock = (
    block: CommunicationBlockDefinition,
): block is LaunchCommunicationBlockDefinition => block.kind === CommunicationBlockKind.LAUNCH;

/**
 * The five block ids routed through the `communications` contract — the two legacy types are
 * excluded by type narrowing, so misuse fails at compile time.
 */
export type CommunicationsLayerBlockIdValue = Exclude<
    CommunicationBlockIdValue,
    | typeof CommunicationBlockId.SELF_ASSESSMENT_LAUNCH
    | typeof CommunicationBlockId.FEEDBACK_360_LAUNCH
>;

export const isCommunicationsLayerBlock = (
    block: CommunicationBlockDefinition,
): block is CommunicationBlockDefinition & { id: CommunicationsLayerBlockIdValue } =>
    block.persistenceRoute === CommunicationBlockPersistenceRoute.COMMUNICATIONS_LAYER;

export const isLegacyScheduleBlock = (
    block: CommunicationBlockDefinition,
): block is LaunchCommunicationBlockDefinition =>
    block.persistenceRoute === CommunicationBlockPersistenceRoute.LEGACY_SCHEDULE;

/**
 * The seven blocks in corrected matrix order. Only `MANAGER_DEADLINE` and `DISCUSSION_EMAIL`
 * have no employee card.
 */
export const COMMUNICATION_BLOCK_ORDER: readonly CommunicationBlockDefinition[] = [
    {
        id: CommunicationBlockId.PROJECT_KICK_OFF,
        kind: CommunicationBlockKind.ORPHAN,
        milestoneType: CycleMilestoneType.PROJECT_KICK_OFF,
        labelKey: 'cycleEvaluationCommunications.blocks.projectKickOff.label',
        hasEmployeeCard: true,
        templateSource: CommunicationTemplateSource.MINIMAL,
        persistenceRoute: CommunicationBlockPersistenceRoute.COMMUNICATIONS_LAYER,
    },
    {
        id: CommunicationBlockId.SELF_ASSESSMENT_LAUNCH,
        kind: CommunicationBlockKind.LAUNCH,
        evaluationType: EvaluationType.SELF_EVALUATION,
        labelKey: 'cycleEvaluationCommunications.blocks.selfAssessmentLaunch.label',
        hasEmployeeCard: true,
        templateSource: CommunicationTemplateSource.REUSED,
        persistenceRoute: CommunicationBlockPersistenceRoute.LEGACY_SCHEDULE,
    },
    {
        id: CommunicationBlockId.FEEDBACK_360_LAUNCH,
        kind: CommunicationBlockKind.LAUNCH,
        evaluationType: EvaluationType.EVALUATION_360,
        labelKey: 'cycleEvaluationCommunications.blocks.feedback360Launch.label',
        hasEmployeeCard: true,
        templateSource: CommunicationTemplateSource.REUSED,
        persistenceRoute: CommunicationBlockPersistenceRoute.LEGACY_SCHEDULE,
    },
    {
        id: CommunicationBlockId.MANAGER_ASSESSMENT_LAUNCH,
        kind: CommunicationBlockKind.LAUNCH,
        evaluationType: EvaluationType.MANAGER_EVALUATION,
        labelKey: 'cycleEvaluationCommunications.blocks.managerAssessmentLaunch.label',
        hasEmployeeCard: true,
        templateSource: CommunicationTemplateSource.REUSED,
        persistenceRoute: CommunicationBlockPersistenceRoute.COMMUNICATIONS_LAYER,
    },
    {
        id: CommunicationBlockId.MANAGER_DEADLINE,
        kind: CommunicationBlockKind.ORPHAN,
        milestoneType: CycleMilestoneType.MANAGER_DEADLINE,
        labelKey: 'cycleEvaluationCommunications.blocks.managerDeadline.label',
        hasEmployeeCard: false,
        templateSource: CommunicationTemplateSource.MINIMAL,
        persistenceRoute: CommunicationBlockPersistenceRoute.COMMUNICATIONS_LAYER,
    },
    {
        id: CommunicationBlockId.DISCUSSION_EMAIL,
        kind: CommunicationBlockKind.ORPHAN,
        milestoneType: CycleMilestoneType.DISCUSSION_EMAIL,
        labelKey: 'cycleEvaluationCommunications.blocks.discussionEmail.label',
        hasEmployeeCard: false,
        templateSource: CommunicationTemplateSource.MINIMAL,
        persistenceRoute: CommunicationBlockPersistenceRoute.COMMUNICATIONS_LAYER,
    },
    {
        id: CommunicationBlockId.EVALUATION_CYCLE_CLOSURE,
        kind: CommunicationBlockKind.ORPHAN,
        milestoneType: CycleMilestoneType.EVALUATION_CYCLE_CLOSURE,
        labelKey: 'cycleEvaluationCommunications.blocks.evaluationCycleClosure.label',
        hasEmployeeCard: true,
        templateSource: CommunicationTemplateSource.MINIMAL,
        persistenceRoute: CommunicationBlockPersistenceRoute.COMMUNICATIONS_LAYER,
    },
];

export const getVisibleCommunicationBlocks = (
    checkedEvaluationTypes: readonly EvaluationTypeValue[],
): CommunicationBlockDefinition[] =>
    COMMUNICATION_BLOCK_ORDER.filter(
        (block) =>
            !isLaunchCommunicationBlock(block) ||
            checkedEvaluationTypes.includes(block.evaluationType),
    );
